import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { BUSINESS, SERVICES } from "../data/site";

const links = [
  { name: "Pricing", to: "/pricing" },
  { name: "Book Now", to: "/book" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="A+ Detailing home">
          <Logo />
        </Link>
        <div className="hidden lg:flex items-center gap-8">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-button"
              className="flex items-center gap-1 text-sm text-steel hover:text-white transition-colors py-4"
            >
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full w-80 glass border border-white/10 rounded-xl p-2 shadow-2xl"
                >
                  {SERVICES.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      data-testid={`nav-service-${s.path.replace(/\//g, "")}`}
                      className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-white/5 group"
                    >
                      <span>
                        <span className="block text-sm font-medium text-white">{s.name}</span>
                        <span className="block text-xs text-muted">{s.desc}</span>
                      </span>
                      <span className="text-xs font-mono text-bright">{s.price}</span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.name.toLowerCase().replace(/\s/g, "-")}-link`}
              className="text-sm text-steel hover:text-white transition-colors"
            >
              {l.name}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-phone-button"
            className="flex items-center gap-2 text-sm font-medium text-white border border-white/15 rounded-full px-4 py-2 hover:border-bright hover:text-bright transition-colors"
          >
            <Phone size={14} /> {BUSINESS.phone}
          </a>
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="relative overflow-hidden group text-sm font-semibold bg-bright text-white rounded-full px-5 py-2.5 hover:bg-accent transition-colors"
          >
            <span className="relative z-10 flex items-center gap-1.5">
              Book Appointment <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        </div>

        <button
          className="lg:hidden text-white p-2"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 space-y-1">
              <p className="font-mono text-[11px] uppercase tracking-widest text-bright mb-2">Services</p>
              {SERVICES.map((s) => (
                <Link key={s.path} to={s.path} data-testid={`mobile-service-${s.path.replace(/\//g, "")}`} className="block py-2 text-base text-steel hover:text-white">
                  {s.name}
                </Link>
              ))}
              <div className="h-px bg-white/10 my-4" />
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="block py-2 text-base font-medium text-white">
                  {l.name}
                </Link>
              ))}
              <a href={BUSINESS.phoneHref} className="flex items-center gap-2 py-3 text-bright font-medium">
                <Phone size={16} /> Call {BUSINESS.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
